import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';

import { KnowledgeBaseSectionVm } from 'app/shared/generated/HelpDesk/Models/KnowledgeBaseSectionVm';

/** A section reduced to what the template renders: markup, and a ready made player URL. */
export interface KnowledgeBaseRenderedSection {
	/** Optional heading. Empty for a passage that runs on from the one above it. */
	header: string;
	/** Trix markup, bound through `innerHTML`. */
	content: string;
	/** Empty for a text-only section, which renders no player at all. */
	videoUrl: string;
	/** Labels the iframe. Numbered, because a page can now hold several of them. */
	videoTitle: string;
}

/**
 * The read face of a Knowledge Base document.
 *
 * Rendered by the reading panel AND by the create/edit form's Preview tab, so the
 * two can never visually drift apart. Deliberately takes loose values rather than
 * a `KnowledgeBaseDocumentVm` or a form group — Preview needs to feed it unsaved
 * form state, which is not a saved document.
 */
@Component({
	selector: 'pcg-knowledge-base-doc-view',
	templateUrl: './knowledge-base-doc-view.component.html',
	styleUrls: ['./knowledge-base-doc-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false
})
export class KnowledgeBaseDocViewComponent {

	/** Only used to label the video iframes. The panel header renders the visible title. */
	readonly docTitle = input<string | undefined>(null);
	readonly description = input<string | undefined>(null);
	/** The article body in reading order — each block is a passage of text and the video that illustrates it. */
	readonly sections = input<KnowledgeBaseSectionVm[] | undefined>([]);

	readonly renderedSections = computed<KnowledgeBaseRenderedSection[]>(() => {
		const sections = this.sections() ?? [];

		// Numbered against the videos rather than the sections, so the labels read
		// "video 2 of 3" and not "video 4 of 9" on an article that mixes in prose.
		const videoCount = sections.filter(section => section?.videoId).length;
		let videoNumber = 0;

		return sections.map(section => ({
			header: section?.header ?? ''
			, content: section?.content ?? ''
			, videoUrl: section?.videoId
				? `https://player.vimeo.com/video/${section.videoId}?autopause=1`
				: ''
			, videoTitle: section?.videoId
				? this.buildVideoTitle(++videoNumber, videoCount)
				: ''
		}));
	});

	/** A lone video keeps the plain document title, the way a single video article always has. */
	private buildVideoTitle(videoNumber: number, videoCount: number) {
		const title = this.docTitle() ?? '';
		return videoCount > 1
			? `${title} — video ${videoNumber} of ${videoCount}`
			: title;
	}
}
